import { usePathname } from 'next/navigation';
import styles from './index.module.scss';
import { useState, useRef, ReactNode, useEffect } from 'react';
import { useLang } from '@/app/utils/useLang';

interface TableRow<T> {
    header: string;
    renderCell: (item: T) => ReactNode;
}

interface ScrollableTableProps<T> {
    data: T[];
    rows: TableRow<T>[];
}

function ScrollableTable<T>({ data, rows }: ScrollableTableProps<T>) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const { t } = useLang('CompatibilityTable');

    const handleScroll = (direction: 'left' | 'right') => {
        if (!scrollContainerRef.current) return;

        const container = scrollContainerRef.current;
        const scrollAmount = 400;
        const newPosition =
            direction === 'left'
                ? Math.max(0, scrollPosition - scrollAmount)
                : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);

        container.scrollTo({
            left: newPosition,
            behavior: 'smooth',
        });
        setScrollPosition(newPosition);
    };

    const handleScrollEvent = () => {
        if (scrollContainerRef.current) {
            setScrollPosition(scrollContainerRef.current.scrollLeft);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
        setScrollLeft(scrollContainerRef.current.scrollLeft);
        scrollContainerRef.current.style.cursor = 'grabbing';
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollContainerRef.current.offsetLeft;
        const walk = (x - startX) * 1.5; // 滚动速度倍数
        scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.style.cursor = 'grab';
        }
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            if (scrollContainerRef.current) {
                scrollContainerRef.current.style.cursor = 'grab';
            }
        }
    };

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.style.cursor = 'grab';
        }
    }, []);

    // 获取当前路径
    const pathname = usePathname();

    return (
        <div className={styles.tableWrapper}>
            <div
                className="scrollContainer"
                ref={scrollContainerRef}
                onScroll={handleScrollEvent}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
            >
                <div className="table">
                    {rows.map((row, rowIndex) => (
                        <div key={rowIndex} className="row">
                            <div className={`column-header ${rowIndex === 0 ? 'first-header' : ''}`}>
                                <span style={{ top: pathname?.includes('delta-3-classic-portable-power-station') ? '24px' : '36px' }}>{row.header}</span>
                            </div>
                            {data.map((item, colIndex) => (
                                <div key={colIndex} className={'cell ' + (rowIndex === 0 ? 'header-cell' : '')}>
                                    {row.renderCell(item)}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {!pathname?.includes('delta-3-classic-portable-power-station') && <div className="note">{t('note')}</div>}

            <div className="navigationButtons">
                <button className="navButton" onClick={() => handleScroll('left')} disabled={scrollPosition === 0}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    className="navButton"
                    onClick={() => handleScroll('right')}
                    disabled={
                        scrollContainerRef.current ? scrollPosition >= scrollContainerRef.current.scrollWidth - scrollContainerRef.current.clientWidth : false
                    }
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

export default ScrollableTable;
